import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Pencil } from "lucide-react";
import { api, errorMessage, fileUrl } from "../api/client";
import type { Operator, Part } from "../api/types";
import { Button, Card, cx, Select, Skeleton } from "../components/Controls";
import { DemoTag } from "../components/Badge";
import { EmptyState } from "../components/EmptyState";
import { ImageLightbox } from "../components/ImageLightbox";
import { toast } from "../components/Toast";
import { InspectionsView } from "./InspectionsView";
import { PartFormModal } from "./PartFormModal";
import { TrainingTab } from "./TrainingTab";
import { modelLabel } from "./PartsPage";

function PartOperatorsTab({ part, onChanged }: { part: Part; onChanged: () => void }) {
  const [ops, setOps] = useState<Operator[]>([]);
  const [pick, setPick] = useState("");
  const load = useCallback(() => api.get<Operator[]>("/operators", { active: "true" }).then(setOps).catch(() => undefined), []);
  useEffect(() => {
    load();
  }, [load]);
  const assigned = ops.filter((o) => o.assigned_part_id === part.id);
  const others = ops.filter((o) => o.assigned_part_id !== part.id && o.role === "operator");
  const chosen = others.find((o) => o.id === pick);
  const assign = async () => {
    try {
      await api.put(`/operators/${pick}`, { assigned_part_id: part.id });
      toast.success("Operario asignado");
      setPick("");
      load();
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <div className="space-y-4">
      <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-end">
        <label className="flex-1 text-sm font-medium text-slate-700">Asignar operario
          <Select value={pick} onChange={(e) => setPick(e.target.value)} className="mt-1 block w-full" data-testid="assign-operator-select">
            <option value="">Elegir operario…</option>
            {others.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.username})</option>)}
          </Select>
        </label>
        <Button disabled={!pick} onClick={assign} data-testid="assign-operator-button">Asignar operario</Button>
      </Card>
      {chosen?.assigned_part && <p className="text-sm text-warn" data-testid="assign-operator-warning">Actualmente en pieza {chosen.assigned_part.part_number}</p>}
      {assigned.length === 0 ? <EmptyState title="Sin operarios asignados" text="Asignar un operario para que pueda inspeccionar esta pieza." /> : (
        <Card className="divide-y divide-slate-100" data-testid="part-operators-list">
          {assigned.map((o) => (
            <div key={o.id} className="flex items-center justify-between px-4 py-3 text-sm">
              <span className="inline-flex items-center gap-2">{o.name} {o.is_demo && <DemoTag />}</span>
              <span className="font-mono text-xs text-slate-500">{o.username}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

const TABS = [["entrenamiento", "Entrenamiento"], ["inspecciones", "Inspecciones"], ["operarios", "Operarios"]];

export default function PartDetailPage() {
  const { id } = useParams();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "entrenamiento";
  const [part, setPart] = useState<Part | null>(null);
  const [editing, setEditing] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const load = useCallback(() => api.get<Part>(`/parts/${id}`).then(setPart).catch(() => setMissing(true)), [id]);
  useEffect(() => {
    load();
  }, [load]);

  if (missing && !part) return <EmptyState title="Pieza no encontrada" text="La pieza no existe o no se pudo cargar." action={<Link to="/piezas" className="text-sm underline">Volver a Piezas</Link>} testId="part-not-found" />;
  if (!part) return <div className="space-y-3"><Skeleton className="h-32 w-full" /><Skeleton className="h-64 w-full" /></div>;
  return (
    <div className="animate-rise space-y-6">
      <Link to="/piezas" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-ink" data-testid="back-to-parts-link"><ArrowLeft className="h-4 w-4" /> Piezas</Link>
      <Card className="flex flex-col gap-5 p-5 sm:flex-row" data-testid="part-header">
        {part.reference_image_url ? (
          <img src={fileUrl(part.reference_image_url)} alt="Referencia" className="h-36 w-full cursor-zoom-in rounded-md object-cover sm:w-52" onClick={() => setLightbox(fileUrl(part.reference_image_url))} data-testid="part-header-reference" />
        ) : <div className="flex h-36 w-full items-center justify-center rounded-md bg-slate-100 text-xs text-slate-400 sm:w-52">Sin referencia</div>}
        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-slate-500" data-testid="part-header-number">{part.part_number}</span>
            {part.is_demo && <DemoTag />}
            {!part.active && <span className="text-xs text-slate-400">Inactiva</span>}
          </div>
          <h1 className="font-display text-[28px] font-bold leading-tight sm:text-[32px]" data-testid="part-header-name">{part.name}</h1>
          <div className="flex flex-wrap gap-1.5">
            {part.points.map((p, i) => <span key={i} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{i + 1} · {p}</span>)}
          </div>
          <div className="text-sm" data-testid="part-header-model">{modelLabel(part)}</div>
        </div>
        <Button variant="secondary" size="sm" onClick={() => setEditing(true)} data-testid="edit-part-button"><Pencil className="h-3.5 w-3.5" /> Editar</Button>
      </Card>
      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setSp({ tab: k }, { replace: true })} className={cx("-mb-px border-b-2 px-4 py-2 text-sm font-medium", tab === k ? "border-ink text-ink" : "border-transparent text-slate-500 hover:text-ink")} data-testid={`part-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "entrenamiento" && <TrainingTab part={part} reloadPart={load} />}
      {tab === "inspecciones" && <InspectionsView fixedPartId={part.id} />}
      {tab === "operarios" && <PartOperatorsTab part={part} onChanged={load} />}
      <PartFormModal open={editing} part={part} onClose={() => setEditing(false)} onSaved={setPart} />
      <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
